// Knowledge Base (/kb) — internal, team-shared knowledge base.
//
// Sharing model (enforced server-side; the UI mirrors it): an article is either
// SHARED WITH THE WORKSPACE (status=published — visible to everyone in the
// tenant) or PRIVATE (status=draft — visible only to its owner/creator and
// admins). The dialog surfaces this as an explicit "Visibility" choice, so a
// user can create an article and share it in one go; the published/draft data
// values are unchanged (this is pure labeling on top of the status field, no
// migration). The list the server returns already honors visibility.
//
// Layout: a full-width, searchable Articles list (with an Owner + Visibility
// column). Create/edit happens in a full modal dialog. Only the OWNER or an
// admin may edit/share/delete an article — controls are hidden otherwise (the
// server enforces it regardless). Shared (published) articles also feed ticket
// deflection (/api/kb/suggest suggests them when filing a ticket).
//
// Category management now lives in Settings → Knowledge Base; this page only
// keeps the category <select> in the article dialog so authors can categorize.

import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  chatKb,
  createKbArticle,
  deleteKbArticle,
  deleteKbChat,
  getKbChat,
  getMe,
  listArticleTickets,
  listAssignees,
  listKbArticles,
  listKbCategories,
  listKbChats,
  publishKbArticle,
  submitKbArticleForReview,
  updateKbArticle,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate, formatTicketNumber } from "../lib/format";
import { useTicketNumberFormat } from "../lib/ticketNumbering";
import type {
  Assignee,
  KbArticleListItem,
  KbArticleStatus,
  KbCategoryRow,
  KbChatMessage,
  KbChatSource,
} from "../lib/types";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { RichTextEditor, RichBody, looksLikeHtml } from "../components/RichText";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import { StatusBadge } from "../components/Badge";

const ARTICLES_SORT: Record<string, SortAccessor<KbArticleListItem>> = {
  title: (a) => a.title,
  slug: (a) => a.slug,
  owner: (a) => a.author_name,
  category: (a) => a.category_name,
  visibility: (a) =>
    a.status === "published" ? "Shared" : a.status === "pending_review" ? "Pending review" : "Private",
  tickets: (a) => a.linked_ticket_count ?? 0,
};

/** Small confirm + run helper for destructive row actions (styled dialog). */
function confirmThen(message: string, fn: () => void): void {
  void confirmDialog({ message, danger: true }).then((ok) => ok && fn());
}

/** URL-safe slug suggestion from a title ("How do I reset Wi-Fi?" → "how-do-i-reset-wi-fi"). */
function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/** Visibility/status pill: published=Shared (green), pending_review=amber,
 *  draft=Private. Reflects the 0096 review gate. */
function StatusPill({ status }: { status: KbArticleStatus }) {
  if (status === "published") return <span className="pill ok" title="Visibility">Shared</span>;
  if (status === "pending_review") return <span className="pill warn" title="Visibility">Pending review</span>;
  return <span className="pill" title="Visibility">Private</span>;
}

export function KnowledgeBasePage() {
  const articlesApi = useApi((signal) => listKbArticles(signal), []);
  const categoriesApi = useApi((signal) => listKbCategories(signal), []);
  const meApi = useApi((signal) => getMe(undefined, signal), []);
  const isAdmin = meApi.data?.is_admin ?? meApi.data?.roles.includes("admin") ?? false;
  const myUserId = meApi.data?.user.id ?? null;
  // Approver class (0096): can publish directly / decide reviews. Non-approver
  // authors must route a share through "Submit for review" instead.
  const perms = meApi.data?.permissions ?? [];
  const canApprove = isAdmin || perms.includes("kb.approve") || perms.includes("settings.admin");
  // Roster for the approver picker (only fetched/needed when not an approver).
  const rosterApi = useApi((signal) => listAssignees(signal), []);
  const roster = rosterApi.data?.assignees ?? [];

  // null = closed, "new" = creating, otherwise the id of the open article.
  const [editing, setEditing] = useState<string | null>(null);
  // Opened for reading (row click / KB assistant) vs editing (Edit button). View
  // mode is read-only even for the owner — they switch to edit from the dialog.
  const [readOnlyView, setReadOnlyView] = useState(false);
  const [query, setQuery] = useState("");
  const openView = (id: string) => { setReadOnlyView(true); setEditing(id); };
  const openEdit = (id: string) => { setReadOnlyView(false); setEditing(id); };

  const articles = articlesApi.data?.articles ?? [];

  // Deep-link: /kb?open=<id> (e.g. from global search) opens that article read-only.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const openId = params.get("open");
    if (openId && articles.some((a) => a.id === openId)) {
      setReadOnlyView(true);
      setEditing(openId);
      setParams({}, { replace: true });
    }
  }, [params, articles, setParams]);
  const categories = categoriesApi.data?.categories ?? [];

  const editingArticle = useMemo(
    () => (editing && editing !== "new" ? articles.find((a) => a.id === editing) ?? null : null),
    [editing, articles],
  );

  /** A caller owns an article when they created it; admins always have rights. */
  const canManage = (a: KbArticleListItem): boolean => isAdmin || a.author_id === myUserId;

  return (
    <>
      <PageBar
        crumbs="Knowledge Base"
        actions={
          <button className="btn primary" onClick={() => openEdit("new")}>
            + New article
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Knowledge Base</h1>
            <div className="sub">
              An internal, team-shared knowledge base. Articles shared with your workspace are
              visible to everyone and help deflect tickets; private articles stay visible only to
              their owner.
            </div>
          </div>
        </div>

        <div className="kb-layout">
          <ArticlesCard
            articles={articles}
            loading={articlesApi.loading}
            error={articlesApi.error}
            onRetry={articlesApi.reload}
            query={query}
            onQuery={setQuery}
            canManage={canManage}
            onView={openView}
            onEdit={openEdit}
            onDeleted={() => articlesApi.reload()}
          />
          <aside className="kb-side">
            <KbAssistantCard onOpenArticle={openView} />
          </aside>
        </div>
      </div>

      {editing !== null ? (
        <ArticleDialog
          key={editing} // remount when the target changes
          article={editing === "new" ? null : editingArticle}
          categories={categories}
          canManage={editing === "new" ? true : editingArticle ? canManage(editingArticle) : false}
          canApprove={canApprove}
          roster={roster}
          myUserId={myUserId}
          forceReadOnly={readOnlyView && editing !== "new"}
          onEdit={() => setReadOnlyView(false)}
          onClose={() => setEditing(null)}
          onSaved={(id) => {
            setReadOnlyView(false);
            setEditing(id);
            articlesApi.reload();
          }}
        />
      ) : null}
    </>
  );
}

// ===== Knowledge Base chat assistant ========================================
// A RAG chat over the team's articles: the server answers ONLY from articles the
// caller can see (published + their own drafts), via Workers AI. Each assistant
// turn lists the articles it drew from; clicking one opens it. Pure client state
// (no persistence) — the transcript resets on reload.

interface KbChatTurn extends KbChatMessage {
  /** Sources attached to an assistant turn (the articles it answered from). */
  sources?: KbChatSource[];
  /** Stable React key — avoids cross-chat bubble reuse that array-index keys
   *  cause when the whole transcript is replaced (reset / open a saved chat). */
  _key?: string;
}

let kbTurnSeq = 0;
const kbTurnKey = (): string => `kt${++kbTurnSeq}`;

function KbAssistantCard({ onOpenArticle }: { onOpenArticle: (id: string) => void }) {
  const [turns, setTurns] = useState<KbChatTurn[]>([]);
  const [input, setInput] = useState("");
  // The saved chat this conversation belongs to (0042). Undefined until the
  // first turn creates it server-side; reused on each subsequent turn.
  const [chatId, setChatId] = useState<string | undefined>(undefined);
  const [showHistory, setShowHistory] = useState(false);
  const chatMut = useMutation(chatKb);
  const historyApi = useApi((signal) => listKbChats(signal), []);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the latest turn in view as the conversation grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns, chatMut.pending]);

  async function send(e: FormEvent) {
    e.preventDefault();
    const question = input.trim();
    if (!question || chatMut.pending) return;
    const next: KbChatTurn[] = [...turns, { role: "user", content: question, _key: kbTurnKey() }];
    setTurns(next);
    setInput("");
    // Send only role+content (drop UI-only `sources`); the server re-retrieves.
    const history: KbChatMessage[] = next.map(({ role, content }) => ({ role, content }));
    const res = await chatMut.run(history, chatId);
    if (res) {
      setChatId(res.chat_id); // persisted; first turn returns the new chat id
      setTurns((t) => [...t, { role: "assistant", content: res.answer, sources: res.sources, _key: kbTurnKey() }]);
      historyApi.reload();
    }
  }

  function reset() {
    setTurns([]);
    setInput("");
    setChatId(undefined);
    chatMut.setError(null);
  }

  async function openChat(id: string) {
    const res = await getKbChat(id);
    setChatId(res.chat.id);
    setTurns(res.messages.map((m, i) => ({ role: m.role, content: m.content, _key: `${id}-${i}`, ...(m.sources ? { sources: m.sources } : {}) })));
    setShowHistory(false);
    chatMut.setError(null);
  }

  async function removeChat(id: string) {
    await deleteKbChat(id);
    if (id === chatId) reset();
    historyApi.reload();
  }

  const chats = historyApi.data?.chats ?? [];

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-head">
        <h3>Ask the Knowledge Base</h3>
        <div className="btn-row" style={{ alignItems: "center", flexWrap: "nowrap", flexShrink: 0, marginLeft: "auto", justifyContent: "flex-end" }}>
          {chats.length > 0 ? (
            <button className="btn small" type="button" onClick={() => setShowHistory((v) => !v)}>
              History {showHistory ? "▲" : "▼"}
            </button>
          ) : null}
          {turns.length > 0 ? (
            <button className="btn small" type="button" onClick={reset}>
              New chat
            </button>
          ) : (
            <span
              className="faint"
              title="AI-powered — answers from your knowledge base"
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}
            >
              <span aria-hidden="true">✨</span> AI
            </span>
          )}
        </div>
      </div>
      {showHistory ? (
        <div className="card-body tight" style={{ borderBottom: "1px solid var(--border)", maxHeight: 200, overflowY: "auto" }}>
          <ul className="kb-chat-history">
            {chats.map((ch) => (
              <li key={ch.id} className={`kb-chat-history-item${ch.id === chatId ? " active" : ""}`}>
                <button type="button" className="kb-chat-history-open" onClick={() => openChat(ch.id)} title={ch.title}>
                  <span className="kb-chat-history-title">{ch.title}</span>
                  <span className="faint">{formatDate(ch.updated_at)}</span>
                </button>
                <button
                  type="button"
                  className="person-remove"
                  aria-label={`Delete chat ${ch.title}`}
                  onClick={() => removeChat(ch.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="card-body tight">
        <div
          ref={scrollRef}
          style={{
            maxHeight: 360,
            overflowY: "auto",
            padding: "16px 0",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {turns.length === 0 && !chatMut.pending ? (
            <div className="faint" style={{ textAlign: "center", padding: "8px 0" }}>
              Ask a question and I’ll answer from your workspace’s articles — e.g.{" "}
              <em>“How do I reset my password?”</em>
            </div>
          ) : null}
          {turns.map((t, i) => (
            <KbChatBubble key={t._key ?? i} turn={t} onOpenArticle={onOpenArticle} />
          ))}
          {chatMut.pending ? (
            <div style={{ alignSelf: "flex-start" }}>
              <Spinner label="Thinking…" />
            </div>
          ) : null}
        </div>
        {chatMut.error ? (
          <div style={{ padding: 0 }}>
            <Alert kind="error">{chatMut.error}</Alert>
          </div>
        ) : null}
        <form onSubmit={send} style={{ display: "flex", gap: 8, padding: "12px 0 16px" }}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the knowledge base…"
            aria-label="Ask the knowledge base"
            style={{ flex: 1 }}
            disabled={chatMut.pending}
          />
          <button className="btn primary" type="submit" disabled={chatMut.pending || !input.trim()}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
}

/** One chat turn: a user question or an assistant answer with its source list. */
function KbChatBubble({
  turn,
  onOpenArticle,
}: {
  turn: KbChatTurn;
  onOpenArticle: (id: string) => void;
}) {
  const isUser = turn.role === "user";
  return (
    <div style={{ alignSelf: isUser ? "flex-end" : "flex-start", maxWidth: "85%" }}>
      <div
        style={{
          // Only the user's own turn gets a (brand) bubble. The assistant answer
          // renders as plain readable text — no boxed "canvas" — so it never
          // shows as light-text-on-light. Always theme-aware via --text.
          padding: isUser ? "8px 12px" : "2px 0",
          borderRadius: isUser ? 12 : 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.5,
          background: isUser ? "var(--brand)" : "transparent",
          color: isUser ? "#fff" : "var(--text)",
        }}
      >
        {turn.content}
      </div>
      {turn.sources && turn.sources.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
          {turn.sources.map((s, i) => (
            <button
              key={s.id}
              className="btn small"
              type="button"
              onClick={() => onOpenArticle(s.id)}
              title={`Open “${s.title}”`}
            >
              [{i + 1}] {s.title}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ===== Articles list (full width, searchable) ===============================

function ArticlesCard({
  articles,
  loading,
  error,
  onRetry,
  query,
  onQuery,
  canManage,
  onView,
  onEdit,
  onDeleted,
}: {
  articles: KbArticleListItem[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  query: string;
  onQuery: (q: string) => void;
  canManage: (a: KbArticleListItem) => boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDeleted: () => void;
}) {
  const deleteMut = useMutation(deleteKbArticle);

  // Case-insensitive client-side filter over title, slug, category name, and body.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((a) =>
      [a.title, a.slug, a.category_name ?? "", a.body_md].some((s) => s.toLowerCase().includes(q)),
    );
  }, [articles, query]);

  const { rows: sortedArticles, sort, toggle } = useTableSort(filtered, ARTICLES_SORT);

  return (
    <div className="card">
      <div className="card-head">
        <h3>Articles</h3>
        <span className="faint">{articles.length}</span>
      </div>
      <div className="card-body tight">
        {/* No horizontal padding here — the .tight body already provides the
            18px gutter, so the input spans the full card width (aligned with the
            table below) instead of being doubly inset. */}
        <div style={{ padding: "10px 0 16px" }}>
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search articles by title, slug, category, or content…"
            aria-label="Search articles"
            style={{ width: "100%" }}
          />
        </div>
        {deleteMut.error ? <Alert kind="error">{deleteMut.error}</Alert> : null}
        {loading ? (
          <Loading label="Loading articles…" />
        ) : error ? (
          <ErrorState error={error} onRetry={onRetry} />
        ) : articles.length === 0 ? (
          <EmptyState>No articles yet. Click “New article” to write the first one.</EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState>No articles match “{query}”.</EmptyState>
        ) : (
          <div className="kb-table-wrap">
          <table className="data">
            <thead>
              <tr>
                <SortTh field="title" sort={sort} onSort={toggle}>Title</SortTh>
                <SortTh field="slug" sort={sort} onSort={toggle}>Slug</SortTh>
                <SortTh field="owner" sort={sort} onSort={toggle}>Owner</SortTh>
                <SortTh field="category" sort={sort} onSort={toggle}>Category</SortTh>
                <SortTh field="tickets" sort={sort} onSort={toggle}>Tickets</SortTh>
                <SortTh field="visibility" sort={sort} onSort={toggle}>Visibility</SortTh>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedArticles.map((a) => {
                const manageable = canManage(a);
                return (
                  <tr
                    key={a.id}
                    onClick={() => onView(a.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{a.title}</td>
                    <td className="faint">{a.slug}</td>
                    <td>{a.author_name ?? <span className="faint">—</span>}</td>
                    <td>{a.category_name ?? <span className="faint">—</span>}</td>
                    <td>
                      {a.linked_ticket_count ? (
                        <span className="pill">{a.linked_ticket_count}</span>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td>
                      <StatusPill status={a.status} />
                    </td>
                    <td>
                      <div className="btn-row" style={{ flexWrap: "nowrap", justifyContent: "flex-end" }}>
                        <button
                          className="btn small"
                          onClick={(e) => {
                            e.stopPropagation();
                            manageable ? onEdit(a.id) : onView(a.id);
                          }}
                        >
                          {manageable ? "Edit" : "View"}
                        </button>
                        {manageable ? (
                          <button
                            className="btn small danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmThen(`Delete article "${a.title}"?`, async () => {
                                if (await deleteMut.run(a.id)) onDeleted();
                              });
                            }}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Article create/edit dialog (full modal) ==============================

function ArticleDialog({
  article,
  categories,
  canManage,
  canApprove,
  roster,
  myUserId,
  forceReadOnly = false,
  onEdit,
  onClose,
  onSaved,
}: {
  article: KbArticleListItem | null; // null = new draft
  categories: KbCategoryRow[];
  canManage: boolean; // owner or admin (read-only view otherwise)
  /** Approver class — keeps the direct publish toggle; others submit for review. */
  canApprove: boolean;
  /** Roster used to pick an approver when submitting for review. */
  roster: Assignee[];
  myUserId: string | null;
  /** Opened for reading: read-only even for the owner, who can switch to edit. */
  forceReadOnly?: boolean;
  /** Owner switches a read-only view into edit mode. */
  onEdit?: () => void;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [title, setTitle] = useState(article?.title ?? "");
  const [slug, setSlug] = useState(article?.slug ?? "");
  // Auto-suggest the slug from the title until the author edits the slug field.
  const [slugTouched, setSlugTouched] = useState(article !== null);
  const [categoryId, setCategoryId] = useState(article?.category_id ?? "");
  const [body, setBody] = useState(article?.body_md ?? "");
  // Visibility is sharing surfaced over the existing status: "shared" = published
  // (visible to the whole workspace), "private" = draft (owner + admins only).
  // New articles default to private; the author can flip to shared and publish
  // in one save.
  // For an approver "shared" means published; for a non-approver it means
  // "submit for review". A pending_review article is treated as already shared.
  const [shared, setShared] = useState(article ? article.status !== "draft" : false);
  // Approver chosen when a non-approver submits for review.
  const [approverId, setApproverId] = useState("");
  const isPending = article?.status === "pending_review";

  const createMut = useMutation(createKbArticle);
  const updateMut = useMutation(updateKbArticle);
  const publishMut = useMutation(publishKbArticle);
  const submitMut = useMutation((id: string, input: { approver_id: string; note?: string }) =>
    submitKbArticleForReview(id, input),
  );
  const savePending = createMut.pending || updateMut.pending || publishMut.pending || submitMut.pending;
  const saveError = createMut.error ?? updateMut.error ?? publishMut.error ?? submitMut.error;
  // Read-only when the caller can't manage it, OR when opened just for reading
  // (row click / KB assistant) — an owner switches to edit via the Edit button.
  // A pending_review article is read-only for non-approvers (awaiting decision).
  const readOnly = forceReadOnly || !canManage || (isPending && !canApprove);
  const approverChoices = roster.filter((p) => p.id !== myUserId);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(title));
  }, [title, slugTouched]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (readOnly || !title.trim() || !slug.trim()) return;
    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      body_md: body,
      category_id: categoryId || null,
    };
    // Approver-class authors need an approver before submitting for review.
    if (shared && !canApprove && !approverId) return;
    // Save the content first (create as a draft, or patch the existing row)…
    const saved = article ? await updateMut.run(article.id, payload) : await createMut.run(payload);
    if (!saved) return;
    const id = saved.article.id;
    const wasShared = article ? article.status !== "draft" : false;
    // …then reconcile visibility to the chosen value.
    if (shared !== wasShared) {
      if (shared && !canApprove) {
        // Non-approver wants to share → submit for review (status → pending_review).
        const sub = await submitMut.run(id, { approver_id: approverId });
        if (!sub) return;
      } else {
        // Approver publishes/unpublishes directly (or unshare back to draft).
        const pub = await publishMut.run(id, shared);
        if (!pub) return;
      }
    }
    onSaved(id);
  }

  const titleText = article ? (readOnly ? "View article" : "Edit article") : "New article";

  return (
    <Modal
      title={titleText}
      onClose={onClose}
      wide
      footer={
        <div className="btn-row" style={{ justifyContent: "flex-end", width: "100%" }}>
          <button className="btn" type="button" onClick={onClose}>
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly ? (
            <button
              className="btn primary"
              type="submit"
              form="kb-article-form"
              disabled={savePending || !title.trim() || !slug.trim()}
            >
              {savePending ? (
                <Spinner label="Saving…" />
              ) : shared && !canApprove ? (
                article ? "Save & submit for review" : "Submit for review"
              ) : article ? (
                "Save changes"
              ) : shared ? (
                "Create & share"
              ) : (
                "Create article"
              )}
            </button>
          ) : null}
          {/* In read-only view, the owner/admin can switch to editing. */}
          {readOnly && canManage && onEdit ? (
            <button className="btn primary" type="button" onClick={onEdit}>
              Edit
            </button>
          ) : null}
        </div>
      }
    >
      {saveError ? <Alert kind="error">{saveError}</Alert> : null}
      {article ? (
        <div className="kb-dialog-meta faint" style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
          <span>
            Owner: <strong>{article.author_name ?? "—"}</strong>
          </span>
          <span>
            <StatusPill status={article.status} />
          </span>
          <span>Last updated {formatDate(article.updated_at)}</span>
        </div>
      ) : null}
      {isPending ? (
        <Alert kind="info">
          This article is awaiting review and will publish once an approver approves it.
        </Alert>
      ) : null}
      {readOnly ? (
        // Viewing an article you don't own: read-only, Body front-and-centre.
        <article className="kb-read">
          <h2 className="kb-read-title">{title}</h2>
          <div className="kb-read-body">
            {looksLikeHtml(body) ? <RichBody html={body} /> : <div className="kb-read-plain">{body}</div>}
          </div>
        </article>
      ) : (
      <form id="kb-article-form" onSubmit={onSave}>
        <label className="field">
          <span className="label">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="How do I reset my password?"
            disabled={readOnly}
            required
          />
        </label>
        <label className="field">
          <span className="label">Slug</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            placeholder="how-do-i-reset-my-password"
            disabled={readOnly}
            required
          />
        </label>
        <label className="field">
          <span className="label">Category</span>
          <select
            value={categoryId ?? ""}
            onChange={(e) => setCategoryId(e.target.value)}
            disabled={readOnly}
          >
            <option value="">(uncategorized)</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="field kb-visibility" disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
          <span className="label">Sharing</span>
          <div className="seg" role="radiogroup" aria-label="Article visibility">
            <label className={`seg-opt${!shared ? " active" : ""}`}>
              <input
                type="radio"
                name="kb-visibility"
                checked={!shared}
                onChange={() => setShared(false)}
                disabled={readOnly}
              />
              Private (only me)
            </label>
            <label className={`seg-opt${shared ? " active" : ""}`}>
              <input
                type="radio"
                name="kb-visibility"
                checked={shared}
                onChange={() => setShared(true)}
                disabled={readOnly}
              />
              {canApprove ? "Shared with workspace" : "Submit for review"}
            </label>
          </div>
          {shared && !canApprove ? (
            <label className="field" style={{ marginTop: 8 }}>
              <span className="label">Approver</span>
              <select
                value={approverId}
                onChange={(e) => setApproverId(e.target.value)}
                disabled={readOnly}
                required
              >
                <option value="">Select an approver…</option>
                {approverChoices.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <span className="hint">
            {!shared
              ? "Visible only to you and workspace admins."
              : canApprove
                ? "Visible to everyone in your workspace and used to deflect tickets."
                : "Sent to your chosen approver — it publishes once they approve."}
          </span>
        </fieldset>
        <div className="field">
          <span className="label">Body</span>
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Write the article — format with the toolbar above…"
          />
        </div>
      </form>
      )}
      {article ? <ReferencedTickets articleId={article.id} /> : null}
    </Modal>
  );
}

/** "Referenced by tickets" — tickets linked to this article (two-way KB, 0095).
 *  Staff-only data; the read 403s for non-staff, in which case we render nothing. */
function ReferencedTickets({ articleId }: { articleId: string }) {
  const { data, loading, error } = useApi((s) => listArticleTickets(articleId, s), [articleId]);
  const numFmt = useTicketNumberFormat();
  if (loading) return <Spinner label="Loading tickets…" />;
  if (error) return null; // 403 for non-staff readers — nothing to show.
  const tickets = data?.tickets ?? [];
  return (
    <div className="kb-referenced" style={{ marginTop: 16, borderTop: "1px solid var(--border, #e5e7eb)", paddingTop: 12 }}>
      <div className="label" style={{ marginBottom: 6 }}>
        Referenced by tickets {tickets.length ? <span className="pill">{tickets.length}</span> : null}
      </div>
      {tickets.length === 0 ? (
        <span className="faint">No tickets reference this article.</span>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {tickets.map((t) => (
            <li key={t.ticket_id} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Link to={`/tickets/${t.ticket_id}`}>
                {formatTicketNumber(t.number, numFmt)} {t.subject}
              </Link>
              {t.status ? <StatusBadge code={t.status} /> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Category management lives in Settings → Knowledge Base (SettingsPage.tsx).
// This page keeps only the category <select> in the article dialog above.
